// hooks/useSearchSuggestions.ts
import { useState, useEffect, useCallback, useRef } from 'react';

import { fetchSuggestions } from '@/lib/api/searchApi';
import { useCityContext } from '@/lib/city-context.client';
import { defaultCityCode } from '@/lib/cross-host-urls';
import type { Event } from '../types/event';
import {
  getRecentSearches,
  getPopularSearches,
  addToSearchHistory,
  trackSearchQuery,
} from '../utils/searchStorage';

export interface SearchSuggestion {
  type: 'artist' | 'venue' | 'location' | 'event' | 'recent' | 'popular';
  value: string;
  label: string;
  category?: string;
  /** Only present when `type === 'location'` (WEB-965) — passed through from the API. */
  locationKind?: 'city' | 'neighborhood';
}

/**
 * Snapshot of the API-driven suggestion state surfaced to consumers.
 *
 * `status` semantics (FRE-821):
 * - `idle` — no in-flight request, no cached result (e.g. < 2-char query)
 * - `loading` — request in flight; `suggestions` still reflects the last
 *   successful response so the dropdown does not flicker to empty
 * - `success` — request resolved 2xx; `suggestions` is fresh
 * - `error` — non-429 failure (5xx, network, parse); `suggestions` retained
 * - `rate-limited` — 429 throttled; `suggestions` retained and
 *   `rateLimitNotice` populated so the UI can render a quiet countdown
 *
 * Suggestions are deliberately preserved across the loading→error and
 * loading→rate-limited transitions. Clearing them on a transient blip
 * causes the autocomplete dropdown to collapse mid-keystroke, which reads
 * as "no results" and is the regression FRE-821 set out to fix.
 */
export interface ApiSuggestionsState {
  query: string;
  status: 'idle' | 'loading' | 'success' | 'error' | 'rate-limited';
  suggestions: SearchSuggestion[];
  /**
   * Set only while `status === 'rate-limited'`. The countdown timer is
   * driven by the consumer (so the timer can re-render without forcing
   * the whole hook to re-render once per second).
   */
  rateLimitNotice: { retryAfterSeconds: number } | null;
}

const ROTATING_PLACEHOLDERS = [
  "Search for 'jazz tonight'...",
  "Try 'food festivals'...",
  "Find 'tech meetups'...",
  "Look for 'art galleries'...",
  "Discover 'comedy shows'...",
  "Search 'outdoor concerts'...",
];

const PLACEHOLDER_ROTATION_INTERVAL = 3000; // 3 seconds
const SUGGESTION_DEBOUNCE_MS = 150;

export const useSearchSuggestions = (events: Event[]) => {
  // API-1821 — suggestions are city-scoped. Hosts without a city (apex) fall
  // back to the default market, the same rule cross-host links use.
  const city = (useCityContext()?.code ?? defaultCityCode()).toLowerCase();
  const [currentPlaceholder, setCurrentPlaceholder] = useState(ROTATING_PLACEHOLDERS[0]);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [apiState, setApiState] = useState<ApiSuggestionsState>({
    query: '',
    status: 'idle',
    suggestions: [],
    rateLimitNotice: null,
  });
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rotate placeholder text
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % ROTATING_PLACEHOLDERS.length);
    }, PLACEHOLDER_ROTATION_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setCurrentPlaceholder(ROTATING_PLACEHOLDERS[placeholderIndex]);
  }, [placeholderIndex]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (rateLimitTimerRef.current) {
        clearTimeout(rateLimitTimerRef.current);
      }
    };
  }, []);

  // Generate all possible suggestions from events (client-side fallback)
  const getAllEventSuggestions = useCallback((): SearchSuggestion[] => {
    const artistSuggestions: SearchSuggestion[] = events.map((event) => ({
      type: 'artist',
      value: event.artist,
      label: `🎤 ${event.artist}`,
      category: 'Artists',
    }));

    const venueSuggestions: SearchSuggestion[] = events.map((event) => ({
      type: 'venue',
      value: event.venue,
      label: `🏛️ ${event.venue}`,
      category: 'Venues',
    }));

    const uniqueLocations = Array.from(new Set(events.map((event) => event.location)));
    const locationSuggestions: SearchSuggestion[] = uniqueLocations.map((location) => ({
      type: 'location',
      value: location,
      label: `📍 ${location}`,
      category: 'Locations',
    }));

    const eventSuggestions: SearchSuggestion[] = events.map((event) => ({
      type: 'event',
      value: event.title,
      label: `🎯 ${event.title}`,
      category: 'Events',
    }));

    // Remove duplicates efficiently using Map
    const allSuggestions = [
      ...artistSuggestions,
      ...venueSuggestions,
      ...locationSuggestions,
      ...eventSuggestions,
    ];

    return [
      ...new Map(allSuggestions.map((item) => [`${item.type}|${item.value}`, item])).values(),
    ];
  }, [events]);

  // Get recent and popular searches
  const getHistorySuggestions = useCallback((): SearchSuggestion[] => {
    const recentSearches = getRecentSearches(3);
    const popularSearches = getPopularSearches(3);

    const recentSuggestions: SearchSuggestion[] = recentSearches.map((query) => ({
      type: 'recent',
      value: query,
      label: `🕒 ${query}`,
      category: 'Recent Searches',
    }));

    const popularSuggestions: SearchSuggestion[] = popularSearches.map((query) => ({
      type: 'popular',
      value: query,
      label: `⭐ ${query}`,
      category: 'Popular Searches',
    }));

    return [...recentSuggestions, ...popularSuggestions];
  }, []);

  // Client-side fallback filtering
  const getClientFilteredSuggestions = useCallback(
    (searchQuery: string): SearchSuggestion[] => {
      const query = searchQuery.toLowerCase();
      const allEventSuggestions = getAllEventSuggestions();
      const historySuggestions = getHistorySuggestions();

      const allSuggestions = [...allEventSuggestions, ...historySuggestions];

      const filtered = allSuggestions.filter((suggestion) =>
        suggestion.value.toLowerCase().includes(query),
      );

      // Group by category and limit
      const grouped: { [key: string]: SearchSuggestion[] } = {};
      filtered.forEach((suggestion) => {
        const category = suggestion.category || 'Other';
        if (!grouped[category]) grouped[category] = [];
        if (grouped[category].length < 3) {
          grouped[category].push(suggestion);
        }
      });

      return Object.values(grouped).flat().slice(0, 8);
    },
    [getAllEventSuggestions, getHistorySuggestions],
  );

  // Fetch suggestions from API with debounce and abort.
  //
  // FRE-821: this is the canonical entry-point for the API-driven path.
  // The 4 result kinds map to distinct UX states; in particular, transient
  // failures (kind === 'error') and 429 throttles (kind === 'rate-limited')
  // MUST NOT clear the previously rendered suggestion list — that flicker
  // is what makes the dropdown read as "no results" mid-keystroke.
  const fetchApiSuggestions = useCallback(
    (searchQuery: string): void => {
      // Cancel any pending debounce and in-flight request
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      abortControllerRef.current?.abort();

      if (!searchQuery.trim() || searchQuery.length < 2) {
        // Reset to idle and drop any retained list — at < 2 chars the consumer
        // falls back to history suggestions, so a stale API list would bleed
        // through and look stuck.
        if (rateLimitTimerRef.current) {
          clearTimeout(rateLimitTimerRef.current);
          rateLimitTimerRef.current = null;
        }
        setApiState({ query: '', status: 'idle', suggestions: [], rateLimitNotice: null });
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setApiState((prev) => ({ ...prev, query: searchQuery, status: 'loading' }));

        const result = await fetchSuggestions(searchQuery, city, controller.signal);

        if (controller.signal.aborted || result.kind === 'aborted') return;

        if (result.kind === 'ok') {
          if (rateLimitTimerRef.current) {
            clearTimeout(rateLimitTimerRef.current);
            rateLimitTimerRef.current = null;
          }
          setApiState({
            query: searchQuery,
            status: 'success',
            suggestions: result.suggestions,
            rateLimitNotice: null,
          });
          return;
        }

        if (result.kind === 'rate-limited') {
          // Preserve the previously rendered list; surface a quiet hint with
          // the parsed Retry-After window. Schedule a return-to-success once
          // the window elapses so the hint clears itself without requiring
          // the user to delete + retype.
          if (rateLimitTimerRef.current) {
            clearTimeout(rateLimitTimerRef.current);
          }
          setApiState((prev) => ({
            query: searchQuery,
            status: 'rate-limited',
            suggestions: prev.suggestions,
            rateLimitNotice: { retryAfterSeconds: result.retryAfterSeconds },
          }));
          rateLimitTimerRef.current = setTimeout(() => {
            rateLimitTimerRef.current = null;
            setApiState((prev) =>
              prev.status === 'rate-limited'
                ? { ...prev, status: 'success', rateLimitNotice: null }
                : prev,
            );
          }, result.retryAfterSeconds * 1000);
          return;
        }

        // kind === 'error' — preserve previous list, mark status so the
        // consumer can surface a quiet indicator if it wants to.
        setApiState((prev) => ({
          query: searchQuery,
          status: 'error',
          suggestions: prev.suggestions,
          rateLimitNotice: null,
        }));
      }, SUGGESTION_DEBOUNCE_MS);
    },
    [city],
  );

  // Use a ref so getFilteredSuggestions can read apiState without re-creating
  const apiStateRef = useRef(apiState);
  apiStateRef.current = apiState;

  // Filter suggestions based on query — reads API state, falls back to client-side.
  // Side-effect-free: callers must invoke fetchApiSuggestions separately.
  //
  // FRE-821: also surfaces the cached API list on `error` and `rate-limited`
  // so the dropdown does not flicker to empty on transient failures. The
  // cached list survives a query change too — `loading` reuses the prior
  // suggestions until the next request resolves.
  const getFilteredSuggestions = useCallback(
    (searchQuery: string): SearchSuggestion[] => {
      if (!searchQuery.trim()) {
        return getHistorySuggestions();
      }

      // If we have API suggestions cached for this query (across success,
      // error, rate-limited, and loading-after-success), prefer them — the
      // cached list is fresher than the client-side fallback and avoids the
      // empty-flicker regression.
      const current = apiStateRef.current;
      const hasCachedForQuery = current.query === searchQuery && current.suggestions.length > 0;
      const isSurfaceableStatus =
        current.status === 'success' ||
        current.status === 'error' ||
        current.status === 'rate-limited' ||
        current.status === 'loading';
      if (hasCachedForQuery && isSurfaceableStatus) {
        return current.suggestions.slice(0, 8);
      }

      // Fall back to client-side filtering
      return getClientFilteredSuggestions(searchQuery);
    },
    [getClientFilteredSuggestions, getHistorySuggestions],
  );

  // Handle search actions
  const handleSearch = useCallback((query: string, fromSuggestion: boolean = false) => {
    if (query.trim()) {
      addToSearchHistory(query, fromSuggestion ? 'suggestion' : 'user');
      trackSearchQuery(query);
    }
  }, []);

  // Get example searches for empty state
  const getExampleSearches = useCallback((): string[] => {
    return [
      'jazz tonight',
      'food festivals',
      'tech meetups',
      'comedy shows',
      'outdoor concerts',
      'art galleries',
    ];
  }, []);

  return {
    currentPlaceholder,
    getFilteredSuggestions,
    fetchApiSuggestions,
    apiSuggestions: apiState,
    handleSearch,
    getExampleSearches,
  };
};
